import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@cms/components/data/Button";
import { SystemOTP } from "@cms/components/data/OTP";
import { AccountBlocked } from "@cms/components/ui/AccountBlocked";
import { SystemBackdrop } from "@cms/components/ui/Backdrop";
import { WrappedLoader } from "@cms/components/ui/Loader";
import { useSnackbar } from "@cms/providers/SnackbarProvider";
import api from "@cms/utils/api";
import { TwoFactorFormData, twoFactorSchema } from "@cms/utils/forms/auth";
import { Routes } from "@cms/utils/routes";
import { useAppDispatch, useAppSelector } from "@cms/utils/store/hooks";
import {
  setProfileAction,
  verifyTwoFactorAsyncAction
} from "@cms/utils/store/slices/user.slice";
import {
  ERROR_CAUSES,
  ISystemApiError
} from "@dl/declarations";

const Two2FAPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { showSnackbar } = useSnackbar();
  const { profile, isAuthenticated, isLoading } = useAppSelector((state) => state.user);

  const [authCause, setAuthCause] = useState<string | undefined>(undefined);
  const [setupData, setSetupData] = useState<{ qrCodeDataUrl: string; secret: string } | null>(null);

  const methods = useForm<TwoFactorFormData>({
    defaultValues: {
      code: ""
    },
    mode: "all",
    resolver: yupResolver(twoFactorSchema) as Resolver<TwoFactorFormData>,
  });

  const { handleSubmit, setValue, watch } = methods;

  const code = watch('code');

  useEffect(() => {
    if (isAuthenticated && (!profile.isTwoFactorSecretSetup || !profile.isTwoFactorSecretVerified)) {
      api
        .put<{ qrCodeDataUrl: string; secret: string }>("/auth/2fa/setup")
        .then((res) => {
          dispatch(setProfileAction({ isTwoFactorSecretSetup: true }));
          setSetupData(res.data);
        })
        .catch((err: AxiosError<ISystemApiError>) => {
          showSnackbar(
            err?.response?.data?.message || "Failed to load 2FA setup",
            "error"
          );
        })
    }
  }, [isAuthenticated, profile]);

  const handleChangeCode = (value: string) => {
    setValue('code', value);
  }

  const onSubmit = (data: TwoFactorFormData) => {
    if (!profile.isTwoFactorSecretSetup) {
      showSnackbar("Two-factor authentication is not setup, please contact support", "error");
      return;
    }

    dispatch(verifyTwoFactorAsyncAction({ code: data.code }))
      .then((data) => {
        if (data.meta.requestStatus === 'fulfilled') {
          onSubmitSuccess();
        }
        else {
          onSubmitError(data.payload as AxiosError<ISystemApiError>);
        }
      })
      .catch((error) => {
        onSubmitError(error as AxiosError<ISystemApiError>);
      });
  };

  const onSubmitSuccess = () => {
    if (search) {
      const searchParams = new URLSearchParams(search)

      const redirect_pathname = searchParams.get('redirect_pathname')

      if (redirect_pathname) {
        const redirect_search = searchParams.get('redirect_search')
        const redirect_hash = searchParams.get('redirect_hash')
        navigate(`${redirect_pathname}?${redirect_search || ''}#${redirect_hash || ''}`)
      }
    }
    else {
      navigate(Routes.OVERVIEW);
    }
  }

  const onSubmitError = (error: AxiosError<ISystemApiError>) => {
    const message = error?.response?.data?.message || 'Login failed';
    const cause = error?.response?.data?.cause;
    console.error(error);
    showSnackbar(message, 'error');
    setAuthCause(cause);
  }

  const renderHeader = () => {
    if (!profile.isTwoFactorSecretVerified) {
      return (
        <>
          <h3 className="text-3xl font-semibold">
            Almost there
          </h3>

          <p className="text-center mb-6">
            Scan the QR code below using the Google Authenticator app and <br /> locate the DL CMS code.
          </p>

          {setupData?.qrCodeDataUrl && (
            <div className="flex justify-center mb-6">
              <img
                src={setupData.qrCodeDataUrl}
                alt="2FA QR Code"
                className="w-48 h-48 border border-gray-200 rounded"
              />
            </div>
          )}
        </>
      )
    }

    return (
      <>
        <h3 className="text-3xl font-semibold">
          Almost there
        </h3>

        <p className="mb-6">
          Check your Google Authenticator and find the DL CMS code.
        </p>
      </>
    )
  }

  const renderFooter = () => {
    if (!profile.isTwoFactorSecretVerified) {
      return (
        <>
          {setupData?.secret && (
            <p className="text-sm text-center mb-4 break-all">
              Can&apos;t scan? Enter this secret manually:<br />
              <code className="text-white font-semibold tracking-wide px-1 rounded select-all">{setupData.secret}</code>
            </p>
          )}
        </>
      )
    }

    return null;
  }

  if (authCause === ERROR_CAUSES.AUTH_BLOCKED) {
    return (
      <AccountBlocked />
    )
  }

  return (
    <>

      <SystemBackdrop isOpen={isLoading}>
        <WrappedLoader loaderSize='lg' loaderColor='white' />
      </SystemBackdrop>

      {renderHeader()}

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <SystemOTP
            length={6}
            value={code}
            onChange={handleChangeCode}
          />

          <Button
            type="submit"
            isLoading={isLoading}
            inputVariant="white_text_blue_bg"
            inputSize="full"
          >
            Verify
          </Button>
        </form>
      </FormProvider>

      {renderFooter()}
    </>
  );
};

export default Two2FAPage;
